<!--
  Header: navigation, the annotator identity, and the two honesty indicators —
  configuration blockers and the palette font check.
-->
<script lang="ts">
  import { DEFAULT_HOME_TAB } from '../lib/homeTabs';
  import { href, router } from '../lib/router.svelte';
  import { annotator } from '../lib/stores/annotator.svelte';
  import { auth } from '../lib/stores/auth.svelte';
  import { app } from '../lib/stores/app.svelte';

  // The identity is captured by `IdentityDialog`, not by an input in this bar. Two inputs
  // wedged between a nav and two status pills were easy to walk past — which is how a session
  // started with no id at all, silently blocking both drawing and the image lock. The header's
  // job is to *show* the identity and give an obvious way back to the dialog.
  const route = $derived(router.route.name);
</script>

<header>
  <div class="row wrap bar">
    <!--
      A real anchor, not a <div onclick>. The brand is the conventional way back to the start
      of an app, and a div with a click handler is unreachable by keyboard, invisible to a
      screen reader and not middle-clickable. `:focus-visible` styling below is part of that,
      not decoration.
    -->
    <!--
      `DEFAULT_HOME_TAB`, not a hardcoded `dashboard`. `href` gives the default tab the bare
      `#/` and every other tab its own address, so naming the tab literally here meant the
      brand pointed at `#/home/dashboard` the moment the default moved — turning the one link
      everybody uses to get back to the start into a link to a specific panel.
    -->
    <a
      class="title"
      href={href({ name: 'home', tab: DEFAULT_HOME_TAB })}
      aria-label="Hismaic annotator — home"
    >
      Hismaic annotator
    </a>
    <nav class="row small">
      <a href={href({ name: 'home', tab: DEFAULT_HOME_TAB })} class:current={route === 'home'}>
        Home
      </a>
      <!--
        Two entries, because there are two questions. "Work queue" answers "what should I do
        next" and opens on the task chooser; "Browse" answers "show me this particular stone"
        and opens on every record there is. One link used to do both, and the screen behind it
        did neither well.
      -->
      <a href={href({ name: 'queue', task: null })} class:current={route === 'queue'}>
        Work queue
      </a>
      <a href={href({ name: 'browse' })} class:current={route === 'browse'}>Browse</a>
      <!--
        Settings is **not** here any more *(moved 2026-08-09)*.

        It was one of four top-level destinations, next to Browse, and it is an operator
        console: an S3 bucket, an AWS profile, an access key id and secret, a presign TTL, and
        eight absolute filesystem paths. An epigrapher never opens it. Sitting in the primary
        nav it told every annotator that this is a system they administer rather than a tool
        they use, which is a share of "overwhelming" that costs nothing to give back.

        It lives beside Sign out now, with the other account-shaped things. Nothing about its
        reachability changed: `#/settings` is unmoved, the config-blocker banners still link
        straight to it, and `ImageCanvas` still deep-links there on `s3_not_configured`.
      -->
    </nav>
    <span class="spacer"></span>

    {#if app.status && !app.status.s3_configured}
      <span class="pill warn" title="Imagery cannot load until a bucket is configured">
        no imagery bucket
      </span>
    {/if}

    {#if auth.signedIn}
      {#if auth.isAdmin}
        <a class="subtle small" href="#/admin" title="Accounts and invitations">Accounts</a>
      {/if}
      <a
        class="subtle small"
        class:current={route === 'settings'}
        href={href({ name: 'settings' })}
        title="Imagery source, credentials and the effective configuration"
      >
        Settings
      </a>
      <span class="pill muted small" title="Signed in. Every box you draw carries this name.">
        {auth.displayName}
        <span class="mono">({annotator.id || 'no annotator id'})</span>
        · {auth.role}
      </span>
      <button
        class="subtle small"
        title="Sign out. Unsaved boxes are kept in this browser for when you return."
        onclick={() => {
          auth.clear();
          router.go({ name: 'login' });
        }}
      >
        Sign out
      </button>
    {/if}
  </div>

  {#if auth.signedIn && annotator.readOnlyAccount}
    <div class="banner warn small row wrap">
      <span>
        Your account is a <strong>viewer</strong>: you can read every inscription, every box and
        every number, and you cannot draw or change one. That is a role, not a missing setting —
        ask an administrator if you need to annotate.
      </span>
    </div>
  {:else if auth.signedIn && !annotator.id}
    <div class="banner warn small row wrap">
      <span>
        This account has no annotator identity linked to it, so the service will refuse a box
        drawn here rather than store one nobody can attribute. An administrator has to link one
        before you can annotate.
      </span>
    </div>
  {/if}

  {#if app.fontCheck && app.fontCheck.missing.length}
    <div class="banner warn small">
      <strong>The palette font is missing {app.fontCheck.missing.length} glyph(s):</strong>
      <span class="glyph">{app.fontCheck.missing.join(' ')}</span>
      — they will render as empty boxes and the palette will be unreadable. Install a font with
      full Latin transliteration coverage (Charis SIL, Gentium Plus, Brill or DejaVu).
    </div>
  {/if}

  {#if app.fatal}
    <div class="banner error small">
      <strong>{app.fatal.code}</strong>
      <p>{app.fatal.message}</p>
      <button class="subtle" onclick={() => app.dismissFatal()}>Dismiss</button>
    </div>
  {/if}
</header>

<style>
  header {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 8px 12px;
    border-bottom: 1px solid var(--line);
    background: var(--bg-panel);
  }

  .bar {
    gap: 14px;
  }

  .title {
    font-size: 1rem;
    font-weight: 600;
    color: var(--text);
    text-decoration: none;
    padding: 2px 6px;
    margin-left: -6px;
    border-radius: 4px;
  }

  .title:hover {
    background: var(--bg-raised);
  }

  .title:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }

  nav {
    gap: 10px;
  }

  nav a {
    text-decoration: none;
    padding: 2px 6px;
    border-radius: 4px;
    color: var(--text-dim);
  }

  nav a.current {
    color: var(--text);
    background: var(--bg-raised);
  }

  .pill {
    border: 1px solid var(--line-strong);
    border-radius: 999px;
    padding: 1px 9px;
    font-size: var(--text-xs);
  }

  .pill.warn {
    border-color: var(--warn);
    color: var(--warn);
  }

  p {
    margin: 2px 0;
  }
</style>
